import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import Login from './pages/Login';
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import Parcels from './pages/Parcels';
import ParcelDetail from './pages/ParcelDetail';
import NewParcel from './pages/NewParcel';
import Clients from './pages/Clients';
import ClientDetail from './pages/ClientDetail';
import DuplicateClients from './pages/DuplicateClients';
import Pricing from './pages/Pricing';
import Drivers from './pages/Drivers';
import DriverMap from './pages/DriverMap';
import Reports from './pages/Reports';
import Billing from './pages/Billing';
import Invoices from './pages/Invoices';
import ReceivePayment from './pages/ReceivePayment';
import CashUp from './pages/CashUp';
import Fuel from './pages/Fuel';
import BankImport from './pages/BankImport';
import MonthEnd from './pages/MonthEnd';
import PastelExport from './pages/PastelExport';
import VatReport from './pages/VatReport';
import Revenue from './pages/Revenue';
import SalesReport from './pages/SalesReport';
import PaymentsReport from './pages/PaymentsReport';
import ProfitLoss from './pages/ProfitLoss';
import Uninvoiced from './pages/Uninvoiced';
import DebtCollection from './pages/DebtCollection';
import DriverPay from './pages/DriverPay';
import Settings from './pages/Settings';
import ActivityLog from './pages/ActivityLog';
import EmailLog from './pages/EmailLog';
import BarcodeGenerator from './pages/BarcodeGenerator';
import LabelTracking from './pages/LabelTracking';
import Runsheet from './pages/Runsheet';
import MissingAddresses from './pages/MissingAddresses';
import TrackParcel from './pages/TrackParcel';
import Layout from './components/Layout';
import { can } from './auth';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30000 } },
});

function RequireAuth({ children }) {
  const token = localStorage.getItem('token');
  if (!token) return <Navigate to="/login" replace />;
  return children;
}

// Hiding a menu item is not access control — typing the URL must fail too.
// The API is the real enforcement point; this just avoids showing a page that
// would only return 403s. `perm` is a permission key from Settings → Roles.
function Require({ perm, children }) {
  if (!can(perm)) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-10 text-center">
        <p className="text-lg font-semibold text-gray-700">Not available for your login</p>
        <p className="text-sm text-gray-400 mt-1">
          Your role does not include this part of the system. An admin can add it under Settings → Roles.
        </p>
      </div>
    );
  }
  return children;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Toaster position="top-right" />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/track/:tracking" element={<TrackParcel />} />
          <Route element={<RequireAuth><Layout /></RequireAuth>}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/parcels" element={<Require perm="parcels.view"><Parcels /></Require>} />
            <Route path="/parcels/new" element={<Require perm="parcels.book"><NewParcel /></Require>} />
            <Route path="/parcels/:tracking" element={<Require perm="parcels.view"><ParcelDetail /></Require>} />
            <Route path="/suppliers" element={<Navigate to="/clients" replace />} />
            <Route path="/clients" element={<Require perm="clients.view"><Clients /></Require>} />
            {/* static segment must be declared before /clients/:id */}
            <Route path="/clients/duplicates" element={<Require perm="clients.duplicates"><DuplicateClients /></Require>} />
            <Route path="/clients/missing-addresses" element={<Require perm="parcels.assign"><MissingAddresses /></Require>} />
            <Route path="/clients/:id" element={<Require perm="clients.view"><ClientDetail /></Require>} />
            <Route path="/pricing" element={<Navigate to="/settings" replace />} />
            <Route path="/drivers" element={<Require perm="parcels.assign"><Drivers /></Require>} />
            <Route path="/drivers/map" element={<Require perm="drivers.track"><DriverMap /></Require>} />
            <Route path="/barcodes" element={<Require perm="labels.manage"><BarcodeGenerator /></Require>} />
            <Route path="/label-tracking" element={<Require perm="labels.manage"><LabelTracking /></Require>} />
            <Route path="/runsheets" element={<Require perm="parcels.assign"><Runsheet /></Require>} />
            <Route path="/reports" element={<Require perm="reports.operational"><Reports /></Require>} />
            <Route path="/billing" element={<Require perm="billing.view"><Billing /></Require>} />
            <Route path="/payments" element={<Require perm="billing.payments"><ReceivePayment /></Require>} />
            <Route path="/invoices" element={<Require perm="billing.view"><Invoices /></Require>} />
            <Route path="/cash-up" element={<Require perm="billing.payments"><CashUp /></Require>} />
            <Route path="/fuel" element={<Require perm="reports.financial"><Fuel /></Require>} />
            <Route path="/driver-pay" element={<Require perm="reports.financial"><DriverPay /></Require>} />
            <Route path="/bank-import" element={<Require perm="billing.bank"><BankImport /></Require>} />
            <Route path="/month-end" element={<Require perm="billing.statements"><MonthEnd /></Require>} />
            <Route path="/pastel-export" element={<Require perm="reports.financial"><PastelExport /></Require>} />
            <Route path="/reports/vat" element={<Require perm="reports.financial"><VatReport /></Require>} />
            <Route path="/reports/revenue" element={<Require perm="reports.financial"><Revenue /></Require>} />
            <Route path="/reports/sales" element={<Require perm="reports.financial"><SalesReport /></Require>} />
            <Route path="/reports/payments" element={<Require perm="reports.financial"><PaymentsReport /></Require>} />
            <Route path="/reports/income-expenses" element={<Require perm="reports.financial"><ProfitLoss /></Require>} />
            <Route path="/reports/uninvoiced" element={<Require perm="billing.view"><Uninvoiced /></Require>} />
            <Route path="/debt-collection" element={<Require perm="billing.payments"><DebtCollection /></Require>} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/activity-log" element={<Require perm="system.audit"><ActivityLog /></Require>} />
            <Route path="/email-log" element={<Require perm="system.audit"><EmailLog /></Require>} />
          </Route>
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
